import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { MensagemDiretaKeys } from './MensagemDireta.keys'
import { podeEnviar } from './MensagemDireta.regras'
import { MensagemDiretaScope, Parceiro, RECADOS_POR_PAGINA } from './MensagemDireta.scopes'
import { MensagemDiretaService } from './MensagemDireta.service'

const LOG = Logger.get('MensagemDiretaPresenter')
const service = MensagemDiretaService.singleton()
const appStorage = AppStorage.singleton()

// Espera o operador parar de digitar antes de consultar o servidor.
const DEBOUNCE_MS = 300

// Fala com o parceiro em nome da AG, pelo número da LIA: só admin, como no servidor.
export const ROLES_AUTORIZADOS = new Set(['admin'])

export function podeMandarMensagemDireta() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

function mensagemDeNegocio(error: unknown): string | undefined {
    if (error instanceof ServiceError && [400, 404].includes(error.statusCode)) {
        return error.message
    }
    return undefined
}

export class MensagemDiretaPresenter extends CubePresenter<MainPresenter, MensagemDiretaScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    private buscaTimer?: ReturnType<typeof setTimeout>
    private buscaParceiroTimer?: ReturnType<typeof setTimeout>
    // Descarta a resposta de uma busca de parceiro que chegou depois de outra mais nova.
    private buscaParceiroSeq = 0

    constructor(app: MainPresenter) {
        super(app, new MensagemDiretaScope())
    }

    public override release() {
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        if (this.buscaParceiroTimer) clearTimeout(this.buscaParceiroTimer)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new MensagemDiretaKeys(this.app, intent)

        if (initialization) {
            if (!podeMandarMensagemDireta()) {
                this.app.alert('warning', 'Acesso restrito', 'A mensagem direta é restrita a administradores.')
                keys.allow = false
                return keys.allow
            }
            this.parentSlot = keys.parentSlot
            this.#initialize()
            await this.#carregarLista()
        }

        this.parentSlot(this.scope)
        return keys.allow
    }

    #initialize() {
        const { scope } = this
        scope.onAtualizar = this.action(this.#carregarLista, this)
        scope.onBuscaChange = this.action(this.#handleBuscaChange, this)
        scope.onPaginaChange = this.action(this.#handlePaginaChange, this)

        scope.onAbrirNova = this.action(this.#handleAbrirNova, this)
        scope.onFecharNova = this.action(this.#handleFecharNova, this)
        scope.onBuscaParceiroChange = this.action(this.#handleBuscaParceiroChange, this)
        scope.onParceiroChange = this.action(this.#handleParceiroChange, this)
        scope.onTextoChange = this.action(this.#handleTextoChange, this)
        scope.onConversaToggle = this.action(this.#handleConversaToggle, this)
        scope.onEnviar = this.action(this.#handleEnviar, this)

        scope.onAbrirCancelar = this.action(this.#handleAbrirCancelar, this)
        scope.onFecharCancelar = this.action(this.#handleFecharCancelar, this)
        scope.onConfirmarCancelar = this.action(this.#handleConfirmarCancelar, this)
    }

    // :: Lista

    async #carregarLista() {
        this.scope.carregandoLista = true
        try {
            const pagina = await service.listar(
                this.scope.busca.trim(),
                this.scope.pagina * RECADOS_POR_PAGINA,
                RECADOS_POR_PAGINA
            )
            this.scope.total = pagina.total
            this.scope.recados = pagina.itens
        } catch (error) {
            this.unexpected('Carregando os recados', error)
        } finally {
            this.scope.carregandoLista = false
        }
    }

    async #handleBuscaChange(texto: string) {
        this.scope.busca = texto
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        this.buscaTimer = setTimeout(() => {
            this.buscaTimer = undefined
            this.scope.pagina = 0
            this.#carregarLista().catch((error) => LOG.error('Buscando recados', error))
        }, DEBOUNCE_MS)
    }

    async #handlePaginaChange(pagina: number) {
        this.scope.pagina = pagina
        await this.#carregarLista()
    }

    // :: Nova mensagem

    /** Abre o diálogo; com o ownerId, já com o parceiro escolhido (o "Nova para ele" da lista). */
    async #handleAbrirNova(ownerId: string) {
        const { scope } = this
        scope.buscaParceiro = ''
        scope.opcoesParceiro = []
        scope.parceiro = undefined
        scope.texto = ''
        scope.erroEnvio = undefined
        scope.conversaAberta = false
        scope.conversa = []
        scope.novaAberta = true
        if (ownerId) {
            try {
                await this.#escolherParceiro(await service.parceiro(ownerId))
            } catch (error) {
                this.unexpected('Carregando o parceiro', error)
            }
        }
    }

    async #handleFecharNova() {
        if (this.scope.enviando) return
        this.scope.novaAberta = false
    }

    async #handleBuscaParceiroChange(texto: string) {
        this.scope.buscaParceiro = texto
        if (this.buscaParceiroTimer) clearTimeout(this.buscaParceiroTimer)
        if (texto.trim().length < 2) {
            this.scope.opcoesParceiro = this.scope.parceiro ? [this.scope.parceiro] : []
            return
        }
        this.buscaParceiroTimer = setTimeout(() => {
            this.buscaParceiroTimer = undefined
            this.#buscarParceiros(texto).catch((error) => LOG.error('Buscando parceiros', error))
        }, DEBOUNCE_MS)
    }

    async #buscarParceiros(texto: string) {
        const seq = ++this.buscaParceiroSeq
        this.scope.buscandoParceiros = true
        try {
            const opcoes = await service.buscarParceiros(texto.trim())
            if (seq === this.buscaParceiroSeq) this.scope.opcoesParceiro = opcoes
        } catch (error) {
            this.unexpected('Buscando parceiros', error)
        } finally {
            if (seq === this.buscaParceiroSeq) this.scope.buscandoParceiros = false
        }
    }

    async #handleParceiroChange(parceiro: Parceiro | null) {
        await this.#escolherParceiro(parceiro ?? undefined)
    }

    async #escolherParceiro(parceiro: Parceiro | undefined) {
        const { scope } = this
        scope.parceiro = parceiro
        scope.erroEnvio = undefined
        scope.conversa = []
        if (parceiro && !scope.opcoesParceiro.some((p) => p.ownerId === parceiro.ownerId)) {
            scope.opcoesParceiro = [parceiro, ...scope.opcoesParceiro]
        }
        if (parceiro && scope.conversaAberta) await this.#carregarConversa()
    }

    async #handleTextoChange(texto: string) {
        this.scope.texto = texto
        this.scope.erroEnvio = undefined
    }

    async #handleConversaToggle() {
        this.scope.conversaAberta = !this.scope.conversaAberta
        if (this.scope.conversaAberta && this.scope.parceiro && !this.scope.conversa.length) {
            await this.#carregarConversa()
        }
    }

    async #carregarConversa() {
        const parceiro = this.scope.parceiro
        if (!parceiro) return
        this.scope.carregandoConversa = true
        try {
            const conversa = await service.conversa(parceiro.ownerId)
            if (this.scope.parceiro?.ownerId === parceiro.ownerId) this.scope.conversa = conversa
        } catch (error) {
            this.unexpected('Carregando a conversa do parceiro', error)
        } finally {
            this.scope.carregandoConversa = false
        }
    }

    async #handleEnviar() {
        const { scope } = this
        if (!podeEnviar(scope.parceiro, scope.texto) || scope.enviando || !scope.parceiro) return

        scope.enviando = true
        try {
            const recado = await service.enviar(scope.parceiro.ownerId, scope.texto)
            scope.novaAberta = false
            this.app.notify(
                'success',
                recado.situacao === 'pendente'
                    ? `Recado para ${recado.parceiro} pendente: sai quando o parceiro responder ao aviso.`
                    : `Recado enviado a ${recado.parceiro}.`,
                recado.situacao === 'pendente' ? 6000 : 3000
            )
            scope.pagina = 0
            await this.#carregarLista()
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) {
                scope.erroEnvio = mensagem
            } else {
                this.unexpected('Enviando o recado', error)
            }
        } finally {
            scope.enviando = false
        }
    }

    // :: Cancelar

    async #handleAbrirCancelar(id: string) {
        this.scope.cancelarAberto = this.scope.recados.find((r) => r.id === id)
    }

    async #handleFecharCancelar() {
        this.scope.cancelarAberto = undefined
    }

    async #handleConfirmarCancelar() {
        const recado = this.scope.cancelarAberto
        if (!recado) return
        this.scope.cancelando = true
        try {
            await service.cancelar(recado.id)
            this.scope.cancelarAberto = undefined
            this.app.notify('success', `Recado para ${recado.parceiro} cancelado.`, 3000)
            await this.#carregarLista()
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            this.scope.cancelarAberto = undefined
            if (mensagem) {
                // Já saiu: o parceiro respondeu enquanto o operador decidia.
                this.app.notify('warning', mensagem, 6000)
                await this.#carregarLista()
            } else {
                this.unexpected('Cancelando o recado', error)
            }
        } finally {
            this.scope.cancelando = false
        }
    }
}

export default MensagemDiretaPresenter
